import React from "react";
import { FileText, AlignLeft, ClipboardList } from "lucide-react";
import {
  INPUT_CLASS,
  INPUT_ERROR_CLASS,
  LABEL_CLASS,
} from "../utils/styles";
import EntityNamePicker from "./EntityNamePicker";

export const INITIAL_METADATA = {
  folderName: "",
  certificateName: "",
  certificateDescription: "",
};

export const METADATA_FIELD_KEYS = [
  "folderName",
  "certificateName",
  "certificateDescription",
];

const CERT_NAME_MAX = 50;
const CERT_DESC_MAX = 100;

function CharCounter({ current, max }) {
  const remaining = max - current;
  const pct = current / max;

  let colorClass;
  if (pct >= 1) {
    colorClass = "text-cfStatusBlockedText font-semibold";
  } else if (pct >= 0.8) {
    colorClass = "text-amber-500 font-medium";
  } else {
    colorClass = "text-cfGlobalText";
  }

  return (
    <span
      className={`text-xs tabular-nums transition-colors duration-150 ${colorClass}`}
      aria-live="polite"
      aria-atomic="true"
    >
      {remaining < 0 ? 0 : remaining} / {max}
    </span>
  );
}

function FieldWrapper({ id, label, helper, error, icon: Icon, required, counter, children }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className={`flex items-center gap-2 ${LABEL_CLASS}`}>
        {Icon && (
          <Icon
            className="w-4 h-4 text-cfPrimary shrink-0"
            strokeWidth={1.75}
          />
        )}
        {label}
        {required && (
          <span className="text-cfStatusBlockedText" aria-hidden="true">*</span>
        )}
      </label>
      {children}
      <div className="flex items-start justify-between gap-2 min-h-[1rem]">
        <div className="flex-1">
          {helper && !error && (
            <p className="text-xs text-cfGlobalText">{helper}</p>
          )}
          {error && (
            <p
              id={`${id}-error`}
              className="text-xs text-cfStatusBlockedText"
              role="alert"
            >
              {error}
            </p>
          )}
        </div>
        {counter}
      </div>
    </div>
  );
}

function CertificateMetadataForm({
  values,
  errors,
  onChange,
  t,
  entitySuggestions,
  isSuggestionsLoading,
  suggestionsError,
  onFetchSuggestions,
  isEntityNameValidating,
  entityNameValidation,
  onValidateEntityName,
  onFolderNameChange,
  entityPickerDisabled = false,
  onDescriptionBlur,
}) {
  const canFetchSuggestions =
    Boolean(values.certificateName?.trim()) &&
    Boolean(values.certificateDescription?.trim());

  const certNameLen = values.certificateName?.length ?? 0;
  const certDescLen = values.certificateDescription?.length ?? 0;

  return (
    <>
      <section
        id="cert-onboarding-metadata"
        className="rounded-xl border border-cfCardBorder bg-cfSecondary shadow-sm p-4 sm:p-6 mb-4"
        aria-labelledby="cert-onboarding-metadata-heading"
      >
        <div className="mb-4 pb-4 border-b border-cfCardBorder flex items-center gap-2.5">
          <div className="shrink-0 w-8 h-8 rounded-lg bg-cfPrimary flex items-center justify-center">
            <ClipboardList className="w-4 h-4 text-cfSecondary" strokeWidth={2} aria-hidden />
          </div>
          <div>
            <h2
              id="cert-onboarding-metadata-heading"
              className="text-base font-semibold text-cfHeadingText leading-tight"
            >
              {t("certificateOnboarding:metadata_section_title")}
            </h2>
            <p className="text-xs text-cfGlobalText mt-0.5">
              {t("certificateOnboarding:metadata_section_subtitle")}
            </p>
          </div>
        </div>


        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          <div className="md:col-span-2">
            <FieldWrapper
              id="cert-onboarding-certificate-name"
              label={t("certificateOnboarding:certificate_name_label")}
              helper={t("certificateOnboarding:certificate_name_helper")}
              error={errors.certificateName}
              icon={FileText}
              required
              counter={
                <CharCounter current={certNameLen} max={CERT_NAME_MAX} />
              }
            >
              <input
                type="text"
                id="cert-onboarding-certificate-name"
                name="certificateName"
                value={values.certificateName}
                onChange={onChange}
                placeholder={t(
                  "certificateOnboarding:certificate_name_placeholder",
                )}
                maxLength={CERT_NAME_MAX}
                required
                aria-required="true"
                className={`${INPUT_CLASS} ${errors.certificateName ? INPUT_ERROR_CLASS : ""
                  }`}
                autoComplete="off"
                aria-describedby={errors.certificateName ? "cert-onboarding-certificate-name-error" : undefined}
              />
            </FieldWrapper>
          </div>

          <div className="md:col-span-2">
            <FieldWrapper
              id="cert-onboarding-certificate-description"
              label={t("certificateOnboarding:certificate_description_label")}
              helper={t("certificateOnboarding:certificate_description_helper")}
              error={errors.certificateDescription}
              icon={AlignLeft}
              required
              counter={
                <CharCounter current={certDescLen} max={CERT_DESC_MAX} />
              }
            >
              <textarea
                id="cert-onboarding-certificate-description"
                name="certificateDescription"
                value={values.certificateDescription}
                onChange={onChange}
                onBlur={onDescriptionBlur}
                placeholder={t(
                  "certificateOnboarding:certificate_description_placeholder",
                )}
                rows={4}
                maxLength={CERT_DESC_MAX}
                required
                aria-required="true"
                className={`${INPUT_CLASS} resize-y min-h-[6rem] ${errors.certificateDescription ? INPUT_ERROR_CLASS : ""
                  }`}
                aria-describedby={errors.certificateDescription ? "cert-onboarding-certificate-description-error" : undefined}
              />
            </FieldWrapper>
          </div>
        </div>
      </section>

      <EntityNamePicker
        folderName={values.folderName}
        onFolderNameChange={onFolderNameChange}
        entitySuggestions={entitySuggestions}
        isSuggestionsLoading={isSuggestionsLoading}
        suggestionsError={suggestionsError}
        onFetchSuggestions={onFetchSuggestions}
        isEntityNameValidating={isEntityNameValidating}
        entityNameValidation={entityNameValidation}
        onValidateEntityName={onValidateEntityName}
        canFetchSuggestions={canFetchSuggestions}
        disabled={entityPickerDisabled}
        error={errors.folderName}
        t={t}
      />
    </>
  );
}

export default CertificateMetadataForm;
